/* eslint-disable react/require-default-props */
import React from 'react';
import moment from 'moment-timezone';
import { TimezoneType } from '../../profile/ProfileTypes';

interface DualTimezoneGutterProps {
  primaryTimezone: TimezoneType;
  secondaryTimezone: TimezoneType;
}

/**
 * Header component rendered above the time gutter columns.
 * Placed via the `timeGutterHeader` components prop on BigCalendar.
 */
export const DualTimezoneGutterHeader = React.memo(({ primaryTimezone, secondaryTimezone }: DualTimezoneGutterProps) => {
  return (
    <div className="rbc-dual-gutter-header" data-testid="DualTimezoneGutterHeader">
      {/* Secondary (outermost / left) */}
      <div className="rbc-dual-gutter-header__col rbc-dual-gutter-header__col--secondary">
        <span className="rbc-dual-gutter-label">{secondaryTimezone.label || secondaryTimezone.name}</span>
      </div>
      {/* Primary (innermost / right, closest to grid) */}
      <div className="rbc-dual-gutter-header__col rbc-dual-gutter-header__col--primary">
        <span className="rbc-dual-gutter-label rbc-dual-gutter-label--primary">
          {primaryTimezone.label || primaryTimezone.name}
        </span>
      </div>
    </div>
  );
});

DualTimezoneGutterHeader.displayName = 'DualTimezoneGutterHeader';

/**
 * Custom TimeSlotWrapper — wraps each time slot in the gutter to inject the
 * secondary timezone label alongside the primary one.
 *
 * react-big-calendar calls this with `{ value: Date, resource: any, children }`.
 * We only care about `value` (the slot's Date) and `children` (the default
 * primary-tz label that RBC renders).
 */
interface TimeSlotWrapperProps {
  value?: Date;
  children?: React.ReactNode;
  resource?: string; // defined for day columns, undefined for gutter
  primaryTimezone: TimezoneType;
  secondaryTimezone: TimezoneType;
}

export const DualTimezoneSlotWrapper = React.memo(
  ({ value, children, primaryTimezone, secondaryTimezone, resource }: TimeSlotWrapperProps) => {
    if (!value || resource !== undefined || !primaryTimezone?.name || !secondaryTimezone?.name) {
      return <>{children}</>;
    }

    /**
     * RBC slots are visual wall-clock positions.
     * DO NOT interpret them as timezone-aware timestamps.
     */

    const localMoment = moment(value);

    const minutes = localMoment.hours() * 60 + localMoment.minutes();

    /**
     * Hide half-hour rows.
     */
    if (localMoment.minutes() === 30) {
      return <div className="rbc-dual-slot-wrapper rbc-dual-slot-wrapper--empty" />;
    }

    /**
     * Create primary timezone visual hour
     * from slot position.
     */
    const primaryTime = moment().tz(primaryTimezone.name).startOf('day').add(minutes, 'minutes');

    /**
     * Secondary timezone is derived from same instant.
     */
    const secondaryTime = primaryTime.clone().tz(secondaryTimezone.name);

    const primaryLabel = primaryTime.format('h:mm A');

    const secondaryLabel = secondaryTime.format('h:mm A');

    return (
      <div className="rbc-dual-slot-wrapper" data-testid="DualTimezoneSlot">
        <div className="rbc-dual-slot__secondary">
          <span className="rbc-dual-slot__label rbc-dual-slot__label--hour">{secondaryLabel}</span>
        </div>

        <div className="rbc-dual-slot__primary">
          <span className="rbc-dual-slot__label rbc-dual-slot__label--primary">{primaryLabel}</span>
        </div>
      </div>
    );
  },
);

DualTimezoneSlotWrapper.displayName = 'DualTimezoneSlotWrapper';
